import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';

interface StripeCheckoutButtonProps {
  priceId: string;
  buttonText: string;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  size?: 'small' | 'medium' | 'large';
  sx?: any;
  successUrl?: string;
  cancelUrl?: string;
}

const StripeCheckoutButton: React.FC<StripeCheckoutButtonProps> = ({
  priceId,
  buttonText,
  variant = 'contained',
  color = 'secondary',
  size = 'large',
  sx,
  successUrl,
  cancelUrl,
}) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    console.log('Button clicked!', { priceId, successUrl, cancelUrl });
    console.log('Environment variables:', {
      REACT_APP_SWE_JOB_PRICE_ID: process.env.REACT_APP_SWE_JOB_PRICE_ID,
      REACT_APP_BEHAVIORAL_QUESTIONS_PRICE_ID: process.env.REACT_APP_BEHAVIORAL_QUESTIONS_PRICE_ID,
      REACT_APP_RESUME_GUIDE_PRICE_ID: process.env.REACT_APP_RESUME_GUIDE_PRICE_ID,
      REACT_APP_LEETCODE_ROADMAP_PRICE_ID: process.env.REACT_APP_LEETCODE_ROADMAP_PRICE_ID,
      REACT_APP_PERSONAL_PLAN_PRICE_ID: process.env.REACT_APP_PERSONAL_PLAN_PRICE_ID,
      REACT_APP_STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    });
    
    // Clean the priceId to remove any newlines or whitespace
    const cleanPriceId = priceId?.trim();
    
    if (!cleanPriceId) {
      alert('No price ID provided! Check environment variables.');
      return;
    }

    // Price ID to value mapping for Meta conversions
    const priceValueMap: Record<string, number> = {
      // LeetCode Roadmap products
      'price_1TjSSxAS9lDGoaEL39amXiJY': 11,  // leetcode-roadmap base
      'price_1TjSSyAS9lDGoaEL7tMwa8Ci': 38,  // leetcode-roadmap-bundle
      'price_1TjSSxAS9lDGoaELzGI6p7Ps': 18,  // leetcode-roadmap-system-design
      'price_1TjSSyAS9lDGoaELUe1EgcSm': 45,  // leetcode-roadmap-full-bundle
      'price_1TjTP6AFlZQpjEwR2CSOmQvk': 0.50, // production checkout test
      'price_1TjTXYAS9lDGoaELPj147EP8': 99, // sandbox personal plan upsell
      'price_1TjTY4AFlZQpjEwRBxERqzwX': 99, // live personal plan upsell
    };

    // Price ID to content name mapping
    const priceContentMap: Record<string, string> = {
      'price_1TjSSxAS9lDGoaEL39amXiJY': 'LeetCode Roadmap',
      'price_1TjSSyAS9lDGoaEL7tMwa8Ci': 'LeetCode Roadmap Bundle',
      'price_1TjSSxAS9lDGoaELzGI6p7Ps': 'LeetCode Roadmap + System Design',
      'price_1TjSSyAS9lDGoaELUe1EgcSm': 'LeetCode Roadmap Full Bundle',
      'price_1TjTP6AFlZQpjEwR2CSOmQvk': 'Production Checkout Test',
      'price_1TjTXYAS9lDGoaELPj147EP8': '1-on-1 Personalized Plan',
      'price_1TjTY4AFlZQpjEwRBxERqzwX': '1-on-1 Personalized Plan',
    };

    // Track InitiateCheckout event before starting the checkout process
    let checkoutValue = priceValueMap[cleanPriceId] || 11;
    let contentName = priceContentMap[cleanPriceId] || 'Digital Product';

    // Fallback logic for other products using env vars
    if (!priceContentMap[cleanPriceId]) {
      if (cleanPriceId === process.env.REACT_APP_SWE_JOB_PRICE_ID) {
        contentName = 'SWE Job Course';
      } else if (cleanPriceId === process.env.REACT_APP_BEHAVIORAL_QUESTIONS_PRICE_ID) {
        if (successUrl && successUrl.includes('product=resume-guide')) {
          contentName = 'Resume Guide';
        } else {
          contentName = 'Behavioral Questions Guide';
        }
      } else if (process.env.REACT_APP_RESUME_GUIDE_PRICE_ID && cleanPriceId === process.env.REACT_APP_RESUME_GUIDE_PRICE_ID) {
        contentName = 'Resume Guide';
      } else if (process.env.REACT_APP_PERSONAL_PLAN_PRICE_ID && cleanPriceId === process.env.REACT_APP_PERSONAL_PLAN_PRICE_ID) {
        contentName = '1-on-1 Personalized Plan';
        checkoutValue = 99;
      }
    }

    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        value: checkoutValue,
        currency: 'USD',
        content_name: contentName,
        content_type: 'product'
      });
    }
    
    setLoading(true);
    
    try {
      const baseUrl = window.location.origin;
      // Always use absolute URLs for Stripe
      const finalSuccessUrl = successUrl?.startsWith('http') 
        ? successUrl 
        : `${baseUrl}${successUrl || '/success'}`;
      const finalCancelUrl = cancelUrl?.startsWith('http') 
        ? cancelUrl 
        : `${baseUrl}${cancelUrl || '/cancel'}`;

      console.log('Making API call to create checkout session...');
      
      const apiUrl = '/api/create-checkout-session';
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: cleanPriceId,
          successUrl: finalSuccessUrl,
          cancelUrl: finalCancelUrl,
          sourceUrl: window.location.href || baseUrl,
        }),
      });

      console.log('API Response:', response.status, response.ok);
      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
        console.log('Parsed response data:', responseData);
      } catch (e) {
        console.error('Failed to parse response as JSON:', e);
        alert(`API Error: ${responseText}`);
        setLoading(false);
        return;
      }

      if (response.ok && responseData.url) {
        console.log('Redirecting to:', responseData.url);
        // Redirect to Stripe Checkout
        window.location.href = responseData.url;
      } else {
        console.error('Failed to create checkout session:', responseData);
        alert('Failed to create checkout session. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      color={color}
      size={size}
      onClick={handleClick}
      disabled={loading}
      sx={{
        position: 'relative',
        ...sx,
      }}
    >
      {loading && (
        <CircularProgress
          size={20}
          sx={{
            position: 'absolute',
            color: variant === 'contained' ? 'white' : color + '.main',
          }}
        />
      )}
      <span style={{ opacity: loading ? 0 : 1 }}>
        {buttonText}
      </span>
    </Button>
  );
};

export default StripeCheckoutButton;
